import { useCallback, useEffect, useRef, useState } from 'react';

import { getResultSummary } from '../../../api/emissionsApi';
import { normalizeResultSummary } from '../../dataInput/model/resultSummary';
import { normalizeHistoricalReportBreakdown, normalizeReportBreakdown } from '../model/reportBreakdown';

const SOURCES = [
  'scope1_fuel',
  'scope2_electricity',
  'scope3_af_report',
  'scope3_ewaste',
];

const EMPTY_STATE = Object.freeze({ rows: [], total: 0, totalPercentage: 0, loading: false, error: null });

/**
 * Loads the report breakdown (Scope 1 by company, Scope 2 by office,
 * Scope 3 by category) by fanning out the four result-summary calls.
 * A sequence guard plus AbortController drop stale responses when the
 * year changes mid-flight. When a historical report payload is supplied,
 * the breakdown is built from it directly instead of calling the screens.
 * When the fan-out fails and summary rows are available, those rows are
 * rendered instead of an error so an empty year still shows the full
 * Scope 1-3 hierarchy including the Scope 3 categories.
 */
export default function useReportBreakdown({
  year,
  enabled,
  historicalReport = null,
  fallbackRows = null,
  fallbackTotal = 0,
  fallbackTotalPercentage = 0,
}) {
  const [state, setState] = useState(EMPTY_STATE);
  const sequenceRef = useRef(0);
  const controllerRef = useRef(null);
  const fallbackRef = useRef({ rows: null, total: 0, totalPercentage: 0 });
  fallbackRef.current = {
    rows: fallbackRows,
    total: fallbackTotal,
    totalPercentage: fallbackTotalPercentage,
  };

  const load = useCallback(async () => {
    if (!year) return;
    const sequence = sequenceRef.current + 1;
    sequenceRef.current = sequence;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setState(current => ({ ...current, loading: true, error: null }));
    try {
      const results = await Promise.all(SOURCES.map((source) => (
        getResultSummary(
          { fiscal_year: Number(year), source },
          { signal: controller.signal },
        ).then(payload => normalizeResultSummary(payload))
      )));
      if (controller.signal.aborted || sequenceRef.current !== sequence) return;
      const [scope1, scope2, scope3Af, scope3Ewaste] = results;
      const breakdown = normalizeReportBreakdown({ scope1, scope2, scope3Af, scope3Ewaste });
      setState({ ...breakdown, loading: false, error: null });
    } catch (loadError) {
      if (controller.signal.aborted || sequenceRef.current !== sequence) return;
      const fallback = fallbackRef.current;
      if (fallback.rows && fallback.rows.length) {
        setState({
          rows: fallback.rows,
          total: fallback.total,
          totalPercentage: fallback.totalPercentage,
          loading: false,
          error: null,
        });
        return;
      }
      setState(current => ({ ...current, loading: false, error: loadError }));
    }
  }, [year]);

  useEffect(() => {
    if (!enabled) {
      sequenceRef.current += 1;
      controllerRef.current?.abort();
      setState(EMPTY_STATE);
      return undefined;
    }
    if (historicalReport) {
      sequenceRef.current += 1;
      controllerRef.current?.abort();
      setState({
        ...normalizeHistoricalReportBreakdown(historicalReport),
        loading: false,
        error: null,
      });
      return undefined;
    }
    load();
    return () => controllerRef.current?.abort();
  }, [enabled, historicalReport, load]);

  return { ...state, reload: load };
}